import { toast } from 'react-toastify';
import { AGENT_BUILDER_RUN_STATES, AGENT_BUILDER_RUN_TRIGGER_TYPES } from '@/agentBuilder/agentBuilder.types';
import { timezoneNaiveDateToMoment } from '@/datetime/flatpickr/flatpickrTimezones.util';
import { warnToast } from '@/utilities/toast.utilities';
import type { TFunction } from 'i18next';
import type {
  AgentBuilderRunAgentNames,
  AgentBuilderRunListItem,
  AgentBuilderRunListParams,
  AgentBuilderRunListRequestParams,
  AgentBuilderRunState,
  AgentBuilderRunTriggerType,
  AgentBuilderUserLabels,
} from '@/agentBuilder/agentBuilder.types';
import type { Timezone } from '@/datetime/timezone.service';
import type {
  AdvancedFilterModel,
  DateFilterModel,
  FilterModel,
  SetFilterModel,
  SortModelItem,
  TextFilterModel,
} from '@seeqdev/ag/grid';

export const normalizeAgentBuilderAgentId = (agentId: string): string => agentId.trim().toLowerCase();

export const normalizeAgentBuilderUserId = (userId: string): string => userId.trim().toLowerCase();

/** Whether two user ids refer to the same user; blank or missing ids match nothing. */
export const isSameAgentBuilderUser = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const normalizedA = a ? normalizeAgentBuilderUserId(a) : '';
  return normalizedA !== '' && normalizedA === (b ? normalizeAgentBuilderUserId(b) : '');
};

/** The resolved display name for a user, or `undefined` while it is loading or if the lookup failed. */
export const agentBuilderUserLabel = (
  userLabelsById: AgentBuilderUserLabels,
  userId: string | null | undefined,
): string | undefined => {
  const trimmedUserId = userId?.trim();
  return trimmedUserId ? userLabelsById[normalizeAgentBuilderUserId(trimmedUserId)] : undefined;
};

/** URL restoration can emit query changes while the first block is loading. */
export const handleRunGridQueryChanged = (
  hasLoadedBlock: boolean,
  cancelPendingRequests: () => Promise<unknown[]>,
  updateUrl: () => void,
): void => {
  if (hasLoadedBlock) {
    void cancelPendingRequests();
  }
  updateUrl();
};

/** Placeholder for values the backend cannot supply until a run finishes. */
export const RUN_VALUE_NONE = '-';

export const RUN_STATE_LABEL_KEYS: Record<AgentBuilderRunState, string> = {
  queued: 'AGENT_BUILDER.RUNS.STATE.QUEUED',
  running: 'AGENT_BUILDER.RUNS.STATE.RUNNING',
  finished: 'AGENT_BUILDER.RUNS.STATE.FINISHED',
  error: 'AGENT_BUILDER.RUNS.STATE.ERROR',
  canceled: 'AGENT_BUILDER.RUNS.STATE.CANCELED',
};

// Colors carry the outcome at a glance: green finished, red error, blue in flight, amber waiting, gray stopped.
export const RUN_STATE_DOT_CLASS_NAMES: Record<AgentBuilderRunState, string> = {
  queued: 'tw:bg-sq-warning',
  running: 'tw:bg-sq-theme-dark',
  finished: 'tw:bg-sq-success',
  error: 'tw:bg-sq-danger',
  canceled: 'tw:bg-sq-gray-text',
};

export const RUN_TRIGGER_LABEL_KEYS: Record<AgentBuilderRunTriggerType, string> = {
  chat: 'AGENT_BUILDER.RUNS.TRIGGER.CHAT',
  schedule: 'AGENT_BUILDER.RUNS.TRIGGER.SCHEDULE',
};

export const runStateLabel = (state: AgentBuilderRunState | undefined, t: TFunction): string =>
  state && RUN_STATE_LABEL_KEYS[state] ? t(RUN_STATE_LABEL_KEYS[state]) : (state ?? RUN_VALUE_NONE);

export const runTriggerLabel = (triggerType: AgentBuilderRunTriggerType | null | undefined, t: TFunction): string =>
  triggerType && RUN_TRIGGER_LABEL_KEYS[triggerType] ? t(RUN_TRIGGER_LABEL_KEYS[triggerType]) : RUN_VALUE_NONE;

/** The agent's display name, or undefined while the agent list is loading or if the agent is unreadable. */
export const runAgentName = (
  data: AgentBuilderRunListItem | undefined,
  agentNamesById: AgentBuilderRunAgentNames,
): string | undefined => {
  const agentId = normalizeAgentBuilderAgentId(data?.customAgentId ?? '');
  return agentId ? agentNamesById.get(agentId) : undefined;
};

/**
 * Accumulates the run users seen so far, returning the previous array untouched when a block introduces no
 * new one. The label lookup keys its work off this identity, so it must only change when the set does.
 */
export const withRunUserIds = (currentUserIds: string[], items: AgentBuilderRunListItem[]): string[] => {
  const knownUserIds = new Set(currentUserIds.map(normalizeAgentBuilderUserId));
  const addedUserIds = items.flatMap((item) => {
    const userId = item.runUserId?.trim();
    if (!userId || knownUserIds.has(normalizeAgentBuilderUserId(userId))) {
      return [];
    }
    knownUserIds.add(normalizeAgentBuilderUserId(userId));
    return [userId];
  });
  return addedUserIds.length > 0 ? [...currentUserIds, ...addedUserIds] : currentUserIds;
};

/** Column ids used by the runs grid; the filter/sort mapping keys off these. */
export const RUN_COLUMN_IDS = {
  startedAt: 'startedAt',
  state: 'state',
  triggerType: 'triggerType',
  agentName: 'agentName',
  runBy: 'runBy',
  durationMs: 'durationMs',
  totalTokens: 'totalTokens',
  openChat: 'openChat',
} as const;

/**
 * Grid column id to the API's `sortField`. Only columns the API can order by are listed; sorting on anything
 * else sends no sortField, leaving the backend's default (newest first) in place.
 */
const SORT_FIELD_BY_COLUMN_ID: Partial<Record<string, string>> = {
  [RUN_COLUMN_IDS.startedAt]: 'startedAt',
  [RUN_COLUMN_IDS.state]: 'state',
  [RUN_COLUMN_IDS.triggerType]: 'triggerType',
  [RUN_COLUMN_IDS.durationMs]: 'durationMs',
  [RUN_COLUMN_IDS.totalTokens]: 'totalTokens',
};

type RunsFilterModel = FilterModel | AdvancedFilterModel | undefined;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value != null;

/** The model for one column, or undefined when the grid is in advanced-filter mode (unused by this table). */
const columnFilter = (filterModel: RunsFilterModel, columnId: string): unknown =>
  isRecord(filterModel) && filterModel.filterType !== 'advanced' ? filterModel[columnId] : undefined;

const isSetFilterModel = (model: unknown): model is SetFilterModel =>
  isRecord(model) && model.filterType === 'set' && Array.isArray(model.values);

const isTextFilterModel = (model: unknown): model is TextFilterModel => isRecord(model) && model.filterType === 'text';

const isDateFilterModel = (model: unknown): model is DateFilterModel => isRecord(model) && model.filterType === 'date';

/** Sentinel for a set filter that is active with nothing selected, which no query parameter can express. */
const MATCHES_NOTHING = Symbol('matchesNothing');

/**
 * The values selected in a set filter, narrowed to the ones the API accepts. A filter absent from the model is
 * no constraint (undefined), and so is a selection covering every option, which is dropped rather than sent as
 * a full list. An active filter with an empty selection means "match nothing" - the API has no parameter for
 * that, so it is reported separately and handled client-side.
 */
const selectedSetValues = <Value extends string>(
  filterModel: RunsFilterModel,
  columnId: string,
  allowed: readonly Value[],
): Value[] | typeof MATCHES_NOTHING | undefined => {
  const filter = columnFilter(filterModel, columnId);
  if (!isSetFilterModel(filter)) {
    return undefined;
  }
  if (filter.values.length === 0) {
    return MATCHES_NOTHING;
  }
  const selected = allowed.filter((option) => filter.values.includes(option));
  return selected.length > 0 && selected.length < allowed.length ? selected : undefined;
};

const setValuesParam = <Value extends string>(
  selection: Value[] | typeof MATCHES_NOTHING | undefined,
): Value[] | undefined => (selection === MATCHES_NOTHING ? undefined : selection);

/**
 * AG Grid date filters carry timezone-naive `YYYY-MM-DD HH:mm:ss` strings. The bound is the wall clock the
 * user typed, so it is read in the same timezone the Started column displays before becoming the UTC instant
 * the API expects. Without a timezone it falls back to the browser's.
 */
const toIsoInstant = (agGridDate: string | null | undefined, timezone: Timezone | undefined): string | undefined => {
  if (!agGridDate) {
    return undefined;
  }
  const naiveDate = new Date(agGridDate.replace(' ', 'T'));
  if (Number.isNaN(naiveDate.getTime())) {
    return undefined;
  }
  return timezone ? timezoneNaiveDateToMoment(naiveDate, timezone).toISOString() : naiveDate.toISOString();
};

/**
 * The Started column's date filter, mapped onto the API's startedAfter/startedBefore bounds, both of which the
 * backend treats as inclusive. The
 * column offers only After / Before / In range, so no other operator needs handling.
 */
const startedBounds = (
  filterModel: RunsFilterModel,
  timezone: Timezone | undefined,
): { after?: string; before?: string } => {
  const filter = columnFilter(filterModel, RUN_COLUMN_IDS.startedAt);
  if (!isDateFilterModel(filter)) {
    return {};
  }
  switch (filter.type) {
    case 'greaterThan':
      return { after: toIsoInstant(filter.dateFrom, timezone) };
    case 'lessThan':
      return { before: toIsoInstant(filter.dateFrom, timezone) };
    case 'inRange':
      return {
        after: toIsoInstant(filter.dateFrom, timezone),
        before: toIsoInstant(filter.dateTo, timezone),
      };
    default:
      return {};
  }
};

/**
 * The Agent column's text filter, resolved into the API's `agentIds`. The API stores runs against agent ids
 * and has no notion of agent names, so the typed text is contains-matched against the names the frontend
 * loaded and only the matching ids are sent. Text that matches no known agent can only match nothing, unless
 * it is itself a full agent id.
 *
 * Without a name map (the agent list could not be loaded) the filter is dropped rather than resolved to an
 * empty match: an unfiltered page is a visibly wrong result the user can retry, while a permanently empty
 * one reads as "this agent has never run".
 *
 * A very short search on a tenant with very many agents sends a correspondingly long `agentIds` list. The
 * full match set is sent rather than a truncated one, since a silent cap would quietly drop runs the user
 * asked to see; a tenant large enough to outgrow the URL needs the API to take this filter in a request body.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const agentIdsMatchingNameFilter = (
  filterModel: RunsFilterModel,
  agentNamesById: AgentNameMap | undefined,
): string[] | typeof MATCHES_NOTHING | undefined => {
  const filter = columnFilter(filterModel, RUN_COLUMN_IDS.agentName);
  if (!isTextFilterModel(filter) || typeof filter.filter !== 'string') {
    return undefined;
  }
  const searchText = filter.filter.trim().toLowerCase();
  if (searchText.length === 0 || !agentNamesById) {
    return undefined;
  }
  const matchingAgentIds = [...agentNamesById]
    .filter(([, agentName]) => agentName.toLowerCase().includes(searchText))
    .map(([agentId]) => agentId);
  // Rows for deleted or inaccessible agents display the raw id, so pasting that id in must match rather than
  // resolve to nothing. Only a full UUID is taken as an id; partial text still resolves through names alone.
  if (UUID_PATTERN.test(searchText) && !matchingAgentIds.includes(searchText)) {
    matchingAgentIds.push(searchText);
  }
  return matchingAgentIds.length > 0 ? matchingAgentIds : MATCHES_NOTHING;
};

const runUserIdsFromFilter = (filterModel: RunsFilterModel): string[] | undefined => {
  const filter = columnFilter(filterModel, RUN_COLUMN_IDS.runBy);
  if (!isTextFilterModel(filter) || typeof filter.filter !== 'string') {
    return undefined;
  }
  const userIds = filter.filter
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  return userIds.length > 0 ? userIds : undefined;
};

/** Agent display names keyed by agent id, as loaded by the page. */
type AgentNameMap = ReadonlyMap<string, string>;

interface RunListParamsRequest {
  offset: number;
  limit: number;
  filterModel?: FilterModel | AdvancedFilterModel;
  sortModel?: SortModelItem[];
  /** Timezone the Started column is displayed in, so its date filter bounds mean the same wall clock. */
  timezone?: Timezone;
  /**
   * Agent names keyed by agent id, used to turn the Agent column's typed text into `agentIds`. Undefined
   * when the agent list is unavailable, which drops the Agent filter rather than matching nothing.
   */
  agentNamesById?: AgentNameMap;
}

/**
 * Translates one AG Grid server-side block request into GET /agent-workflows/runs query parameters.
 * Undefined entries are dropped when the query string is built, so an unset filter is simply absent.
 */
export const buildAgentBuilderRunListParams = async ({
  offset,
  limit,
  filterModel,
  sortModel,
  timezone,
  agentNamesById,
}: RunListParamsRequest): Promise<AgentBuilderRunListRequestParams> => {
  const primarySort = sortModel?.find((column) => column.sort);
  const sortField = primarySort ? SORT_FIELD_BY_COLUMN_ID[primarySort.colId] : undefined;
  const started = startedBounds(filterModel, timezone);
  const states = selectedSetValues(filterModel, RUN_COLUMN_IDS.state, AGENT_BUILDER_RUN_STATES);
  const triggerTypes = selectedSetValues(filterModel, RUN_COLUMN_IDS.triggerType, AGENT_BUILDER_RUN_TRIGGER_TYPES);
  const agentIds = agentIdsMatchingNameFilter(filterModel, agentNamesById);
  const runUserIds = runUserIdsFromFilter(filterModel);

  return {
    offset,
    limit,
    sortField,
    sortAsc: sortField ? primarySort?.sort === 'asc' : undefined,
    states: setValuesParam(states),
    triggerTypes: setValuesParam(triggerTypes),
    agentIds: setValuesParam(agentIds),
    runUserIds,
    startedAfter: started.after,
    startedBefore: started.before,
    matchesNothing:
      states === MATCHES_NOTHING || triggerTypes === MATCHES_NOTHING || agentIds === MATCHES_NOTHING ? true : undefined,
  };
};

/** Serializes run-list parameters, omitting unset values and joining list parameters with commas. */
export const agentBuilderRunListQueryString = (params: AgentBuilderRunListParams): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    // matchesNothing is a client-side directive, not a query parameter.
    if (value == null || key === 'matchesNothing') {
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) {
        query.set(key, value.join(','));
      }
      continue;
    }
    query.set(key, String(value));
  }
  return query.toString();
};

const MILLISECONDS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

/**
 * Humanizes a run duration ("870ms", "41s", "2m 14s", "1h 05m"). Returns undefined when the backend has no
 * duration to report, which the grid renders as a placeholder rather than a misleading zero.
 */
export const formatRunDurationMs = (durationMs: number | null | undefined): string | undefined => {
  if (durationMs == null || !Number.isFinite(durationMs) || durationMs < 0) {
    return undefined;
  }
  if (durationMs < MILLISECONDS_PER_SECOND) {
    return `${Math.round(durationMs)}ms`;
  }
  const totalSeconds = Math.round(durationMs / MILLISECONDS_PER_SECOND);
  if (totalSeconds < SECONDS_PER_MINUTE) {
    return `${totalSeconds}s`;
  }
  const totalMinutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  if (totalMinutes < MINUTES_PER_HOUR) {
    return `${totalMinutes}m ${totalSeconds % SECONDS_PER_MINUTE}s`;
  }
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  return `${Math.floor(totalMinutes / MINUTES_PER_HOUR)}h ${String(minutes).padStart(2, '0')}m`;
};

const VISIBILITY_DEGRADED_TOAST_ID = 'agent-builder-runs-visibility-degraded';

export const warnIfRunVisibilityDegraded = (visibilityDegraded?: boolean): void => {
  if (visibilityDegraded && !toast.isActive(VISIBILITY_DEGRADED_TOAST_ID)) {
    warnToast({
      messageKey: 'AGENT_BUILDER.RUNS.VISIBILITY_DEGRADED',
      toastUUID: VISIBILITY_DEGRADED_TOAST_ID,
    });
  }
};
